import { useState, useEffect, useRef } from 'react'
import { isPrint } from '@/utils/print'
import { cx } from '@/utils/strings'
import { useIntersectionObserver } from 'usehooks-ts'
import ShieldLoader from './shield-loader'
import VimeoChapters from './vimeo-chapters'
import RichText from '@/components/ui/rich-text'
import { useTranslations, useLocale } from '@/utils/locale'
import { usePage } from '@inertiajs/react'
import { TypeComponentVideo } from '@/types/ctf'

// videos supported:
// vimeo example     https://player.vimeo.com/video/632391847
// youtube example   https://www.youtube.com/embed/YhIwT4AMvm0

type VideoProps = TypeComponentVideo<'WITHOUT_UNRESOLVABLE_LINKS'> & {
  className?: string
}

function PrintVideoThumbnail({ url, thumbnailUrl }: { url: string; thumbnailUrl: string }) {
  const [imgFailed, setImgFailed] = useState(false)
  const { t } = useTranslations()

  return (
    <div className="flex flex-col items-center">
      <a href={url} target="_blank" rel="noreferrer" className="flex flex-col items-center">
        {imgFailed ? (
          <div className="aspect-video bg-gray-100 flex items-center justify-center text-gray-400 text-sm w-[640px] max-w-full">
            {t('Video')}
          </div>
        ) : (
          <img
            src={thumbnailUrl}
            alt={t('Video thumbnail')}
            className="max-w-full"
            onError={() => setImgFailed(true)}
          />
        )}
        {url && <p className="text-sm mt-2 text-gray-600">{url}</p>}
      </a>
    </div>
  )
}

export default function Video({ className, fields }: VideoProps) {
  const { url, aspectRatio, showChapters = false, chaptersIntro } = fields
  const [isVimeo, setIsVimeo] = useState(false)
  const scriptRef = useRef<HTMLScriptElement | null>(null)
  const [vimeoLoaded, setVimeoLoaded] = useState(typeof window !== 'undefined' && !!window.Vimeo)

  const page = usePage()

  const { lang } = useLocale(page.url)

  const isEnglish = lang === 'en'

  const { t } = useTranslations()

  // default params dont work because stinker contentful sends null instead of undefined
  const aspectRatioWithDefault = aspectRatio || '16 / 9'

  const isprint = isPrint()

  const { isIntersecting, ref: videoContainer } = useIntersectionObserver({ freezeOnceVisible: true })

  useEffect(() => {
    // Only proceed if it's a Vimeo video, we're in browser context, and the video is intersecting
    if (!url?.toLowerCase().includes('vimeo') || typeof window === 'undefined' || !isIntersecting) {
      return
    }

    // Check if Vimeo SDK is already loaded
    if (window.Vimeo) {
      setVimeoLoaded(true)
      return
    }

    if (!document.querySelector('script#vimeo-player-sdk')) {
      const script = document.createElement('script')
      script.id = 'vimeo-player-sdk'
      script.src = 'https://player.vimeo.com/api/player-2.26.0.js'
      script.integrity = 'sha384-cspqYTlnJGgea0pMlbvC1pz6dOPp5NrryEqRF400Iiukq7L+BeYZenBZ+9Ee9I4F'
      script.crossOrigin = 'anonymous'
      script.async = true

      script.onload = () => {
        setVimeoLoaded(true)
      }

      document.head.appendChild(script)
      scriptRef.current = script
    }
  }, [url, showChapters, isIntersecting])

  // Extract video ID from URL
  useEffect(() => {
    if (url?.toLowerCase().includes('vimeo')) {
      setIsVimeo(true)
    }
  }, [url])

  const extractVideoUrl = (url: string) => {
    const youtubeRegex = /youtube\.com\/embed\/([\w-]{11})/
    const youtubeMatch = youtubeRegex.exec(url)
    if (youtubeMatch) return `https://img.youtube.com/vi/${youtubeMatch[1]}/hqdefault.jpg`

    const vimeoRegex = /vimeo\.com\/video\/(\d+)/
    const vimeoMatch = vimeoRegex.exec(url)
    if (vimeoMatch) return `https://vumbnail.com/${vimeoMatch[1]}.jpg`

    return ''
  }

  const getWatchUrl = (url: string) => {
    const youtubeMatch = /youtube\.com\/embed\/([\w-]{11})/.exec(url)
    if (youtubeMatch) return `https://www.youtube.com/watch?v=${youtubeMatch[1]}`

    const vimeoMatch = /vimeo\.com\/video\/(\d+)/.exec(url)
    if (vimeoMatch) return `https://vimeo.com/${vimeoMatch[1]}`

    return url
  }

  if (isprint) {
    return <PrintVideoThumbnail url={getWatchUrl(url)} thumbnailUrl={extractVideoUrl(url)} />
  }

  return (
    <div className={cx('video-container', className)}>
      <div
        className={cx(
          'w-full',
          aspectRatioWithDefault === '16 / 9' && `aspect-16/9`,
          aspectRatioWithDefault === '9 / 16' && `aspect-9/16`,
          aspectRatioWithDefault === '4 / 3' && `aspect-4/3`,
          aspectRatioWithDefault === '1 / 1' && `aspect-1/1`
        )}
        data-testid="videoplayer"
        ref={videoContainer}
      >
        {isIntersecting ? (
          <>
            {/* Player ratio: 100 / (1280 / 720) */}
            {url?.toLowerCase()?.includes('vimeo') && (
              <iframe
                className="w-full h-full"
                src={`${url}?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479&audiotrack=${lang}&texttrack=${lang}`}
                title={t('Bitwarden video')}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                sandbox="allow-scripts allow-same-origin allow-popups"
              />
            )}
            {url?.toLowerCase()?.includes('youtube') && (
              <iframe
                className="w-full h-full"
                src={`${url}?rel=0&modestbranding=1&hl=${lang}&cc_lang_pref=${lang}&cc_load_policy=1`}
                title={t('Bitwarden video')}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </>
        ) : (
          <div className="flex items-center justify-center w-full h-full">
            <ShieldLoader className="w-auto" />
          </div>
        )}
      </div>

      {showChapters && isVimeo && isEnglish && (
        <div className="mt-4">
          {chaptersIntro ? (
            <RichText body={chaptersIntro} />
          ) : (
            <h3 className="text-lg font-medium mb-2">{t('Video Chapters')}</h3>
          )}
        </div>
      )}
      {/* Show chapters if enabled and it's a Vimeo video */}
      {showChapters && isVimeo && isEnglish && isIntersecting && (
        <div className="mt-4">
          <VimeoChapters videoUrl={url} vimeoLoaded={vimeoLoaded} />
        </div>
      )}
    </div>
  )
}
